
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAuthSession = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          if (error.message.includes("JWT") || error.message.includes("user_not_found")) {
            await supabase.auth.signOut();
          }
          navigate("/auth");
          return;
        }

        if (!session) {
          console.log("No active session");
          navigate("/auth");
          return;
        }

        // Check user role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (roleError) {
          console.error("Error fetching user role:", roleError);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to verify user role",
          });
          return;
        }

        console.log("Session user ID:", session.user.id);
        setUserId(session.user.id);
      } catch (error) {
        console.error("Error checking session:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again."
        });
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setUserId(session.user.id);
      } else {
        setUserId(null);
        
        // Only redirect to auth page if the user is truly signed out
        if (event === "SIGNED_OUT") {
          navigate("/auth");
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  return { userId, loading };
};
